/**
 * Scheduled Tasks Page
 *
 * Main page for viewing and managing scheduled data retrieval tasks
 */

import React, { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ScheduledTasksList } from '../components/scheduled-tasks/ScheduledTasksList';
import { SchedulerDashboard } from '../components/scheduler/SchedulerDashboard';
import { RunningTasksPanel } from '../components/scheduler/RunningTasksPanel';
import { PageWrapper } from "@/components/layout/PageWrapper";
import { SubNav, type SubNavTab } from "@/components/layout/SubNav";
import { ViewGuard } from "@/components/auth/PermissionGuards";

const schedulerTabs: SubNavTab[] = [
  { label: "Scheduled Tasks", path: "/scheduled-tasks" },
  { label: "Execution History", path: "/task-executions" },
];

interface ScheduledTasksPageProps {
  onRefreshReady?: (refreshFn: () => void, isRefreshing: boolean) => void;
  returnPath?: string;
}

export default function ScheduledTasksPage({ onRefreshReady, returnPath }: ScheduledTasksPageProps = {}) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ["/api/scheduled-tasks"] });
    await queryClient.invalidateQueries({ queryKey: ["executionStats"] });
    await queryClient.invalidateQueries({ queryKey: ["rateLimitStatus"] });
    await queryClient.invalidateQueries({ queryKey: ["runningExecutions"] });
    setTimeout(() => setIsRefreshing(false), 500);
  }, [queryClient]);

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(handleRefresh, isRefreshing);
    }
  }, [onRefreshReady, handleRefresh, isRefreshing]);

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view Scheduler</p>
        </div>
      </div>
    }>
      <SubNav
        tabs={schedulerTabs}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />
      <PageWrapper padding="default">
        <SchedulerDashboard />
        <RunningTasksPanel />
        <ScheduledTasksList returnPath={returnPath} />
      </PageWrapper>
    </ViewGuard>
  );
}
